import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import { api } from "@/lib/api";
import { FileText, Send, Ship, Table2, Building2 } from "lucide-react";
import RateCard from "@/components/RateCard";
import AboutSGL from "@/components/AboutSGL";

const TABS = [
  { key: "rate-card", label: "Rate Card", icon: Table2 },
  { key: "quotation", label: "Point to Point Quotation Request", icon: FileText },
  { key: "about", label: "About SGL", icon: Building2 },
];
const CONTAINERS = ["20GP", "40GP", "40HC", "40RF"];
const INCOTERMS = ["FOB", "CIF", "CFR", "EXW", "DAP"];

const statusColor = (s) => (String(s).toLowerCase().includes("quoted") ? "text-emerald-400 border-emerald-400/30" : "text-amber-400 border-amber-400/30");

const EMPTY = { origin: "", destination: "", container_type: "40HC", quantity: 1, commodity: "", incoterm: "FOB", ready_date: "", notes: "" };

export default function Procurement() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const active = TABS.find((t) => t.key === tab) ? tab : "rate-card";
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [quotes, setQuotes] = useState([]);

  const loadQuotes = () => { api.get("/procurement/quotation").then((r) => setQuotes(r.data)).catch(() => {}); };
  useEffect(() => { loadQuotes(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.origin.trim() || !form.destination.trim()) { toast.error("Origin and destination are required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/procurement/quotation", { ...form, quantity: Number(form.quantity) || 1 });
      toast.success("Quotation request submitted", { description: `Ref ${data.ref} · ${data.origin} → ${data.destination}` });
      setForm(EMPTY);
      loadQuotes();
    } catch (err) {
      toast.error("Could not submit", { description: err.response?.data?.detail || err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const field = "w-full bg-secondary/40 border border-border px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors";
  const lbl = "block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2";

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-[1400px] px-6 py-10" data-testid="procurement-page">
        <div className="mb-8 animate-fade-up">
          <div className="font-mono-plex text-[11px] tracking-[0.3em] uppercase text-muted-foreground mb-3">Commercial</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">{TABS.find((t) => t.key === active).label}</h1>
        </div>

        <div className="flex flex-wrap gap-1 border-b border-border mb-8">
          {TABS.map((t) => {
            const Icon = t.icon;
            const on = t.key === active;
            return (
              <button key={t.key} data-testid={`subtab-${t.key}`} onClick={() => navigate(`/procurement/${t.key}`)} className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${on ? "border-[#F59E0B] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                <Icon className="h-4 w-4" strokeWidth={1.5} /> {t.label}
              </button>
            );
          })}
        </div>

        {active === "rate-card" && <RateCard />}
        {active === "about" && <AboutSGL />}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" style={{ display: ["rate-card", "about"].includes(active) ? "none" : undefined }}>
          <form onSubmit={submit} className="lg:col-span-3 border border-border bg-white p-6 space-y-5" data-testid="quote-form">
            <div className="flex items-center gap-2 mb-1">
              <Ship className="h-4 w-4 text-[#F59E0B]" />
              <h3 className="text-base font-semibold tracking-tight">Request a point-to-point quote</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div><label className={lbl}>Origin port</label><input className={field} data-testid="quote-origin" placeholder="e.g. Shanghai, CN" value={form.origin} onChange={set("origin")} /></div>
              <div><label className={lbl}>Destination port</label><input className={field} data-testid="quote-destination" placeholder="e.g. Sydney, AU" value={form.destination} onChange={set("destination")} /></div>
              <div><label className={lbl}>Container type</label><select className={field} data-testid="quote-container" value={form.container_type} onChange={set("container_type")}>{CONTAINERS.map((c) => (<option key={c} value={c}>{c}</option>))}</select></div>
              <div><label className={lbl}>Quantity</label><input type="number" min="1" className={field} data-testid="quote-qty" value={form.quantity} onChange={set("quantity")} /></div>
              <div><label className={lbl}>Commodity</label><input className={field} data-testid="quote-commodity" placeholder="e.g. Packaged food" value={form.commodity} onChange={set("commodity")} /></div>
              <div><label className={lbl}>Incoterm</label><select className={field} data-testid="quote-incoterm" value={form.incoterm} onChange={set("incoterm")}>{INCOTERMS.map((c) => (<option key={c} value={c}>{c}</option>))}</select></div>
              <div><label className={lbl}>Cargo ready date</label><input type="date" className={field} data-testid="quote-ready" value={form.ready_date} onChange={set("ready_date")} /></div>
            </div>
            <div><label className={lbl}>Notes</label><textarea rows={3} className={field} data-testid="quote-notes" placeholder="Special requirements, hazardous, reefer temp, etc." value={form.notes} onChange={set("notes")} /></div>
            <button type="submit" disabled={submitting} data-testid="quote-submit" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors">
              <Send className="h-4 w-4" /> {submitting ? "Submitting…" : "Submit quotation request"}
            </button>
          </form>

          <div className="lg:col-span-2" data-testid="quote-list">
            <h3 className="text-base font-semibold tracking-tight mb-4">Recent requests</h3>
            {quotes.length === 0 ? (
              <p className="text-sm text-muted-foreground border border-dashed border-border p-6">No requests yet. Submit one to see it here.</p>
            ) : (
              <div className="space-y-3">
                {quotes.map((q, i) => (
                  <div key={i} className="border border-border bg-white p-4" data-testid={`quote-item-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono-plex text-sm text-foreground">{q.ref}</span>
                      <span className={`font-mono-plex text-[10px] uppercase tracking-widest border px-2 py-0.5 ${statusColor(q.status)}`}>{q.status}</span>
                    </div>
                    <div className="text-sm mb-1">{q.origin} → {q.destination}</div>
                    <div className="text-xs text-muted-foreground font-mono-plex">{q.quantity} × {q.container_type}{q.incoterm ? ` · ${q.incoterm}` : ""}{q.ready_date ? ` · ready ${q.ready_date}` : ""}</div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground/50 mt-4 font-mono-plex tracking-widest uppercase">Sample — demo submissions</p>
          </div>
        </div>
      </main>
    </div>
  );
}
